/**
 * [Phase 2b-B §4.2 R10 / Phase 2c-D §6.1] Step 1 - League Type & Setup
 *
 * Two-tier selector:
 *   1. What are you setting up? (Draft / Season)
 *   2. If Draft: Auction (default) / Snake - both selectable as of 2c-D
 *      Live or Mock? - collapsed into a single 3-option radio when Mock is chosen (R10):
 *        ◯ Live  ◯ Mock + Season  ◯ Mock only
 *   3. If Season: Live (grayed - Coming Soon) / Mock
 *
 * Maps to `league_type` (one of LEGAL_LEAGUE_TYPES on the backend).
 * Below the type selector: League Name + Commissioner Team Name (NEW-6) +
 * Number of Teams.
 *
 * Top of step (NEW for 2c-D §6.2): "Copy settings from a prior league" button -
 * opens CopySettingsModal which calls replaceForm(parseCopiedSettings(league)).
 */
import { Lock, Copy } from "lucide-react";

// Mapping: (top, draftFormat, liveOrMock) → league_type
export const computeLeagueType = (form) => {
  if (form.top === "season") {
    return form.seasonMode === "live"
      ? "NO_DRAFT_LIVE_SEASON" // grayed in UI; never selectable in 2b-B
      : "NO_DRAFT_MOCK_SEASON";
  }
  // top === "draft"
  if (form.draftMode === "live") return "LIVE_DRAFT_LIVE_SEASON";
  if (form.draftMode === "mock_season") return "MOCK_DRAFT_MOCK_SEASON";
  return "MOCK_DRAFT_ONLY";
};

// [Phase 2c-D §6.2.1 Gotcha (c)] Inverse of computeLeagueType. Bijective mapping
// across 5 legal league_type values + an `undefined` fallback for Phase 1
// legacy leagues (Gotcha d) that pre-date the league_type column.
export const parseLeagueType = (league_type) => {
  switch (league_type) {
    case "LIVE_DRAFT_LIVE_SEASON":
      return { top: "draft", draftFormat: "auction", draftMode: "live", seasonMode: "mock" };
    case "MOCK_DRAFT_MOCK_SEASON":
      return { top: "draft", draftFormat: "auction", draftMode: "mock_season", seasonMode: "mock" };
    case "MOCK_DRAFT_ONLY":
      return { top: "draft", draftFormat: "auction", draftMode: "mock_only", seasonMode: "mock" };
    case "NO_DRAFT_MOCK_SEASON":
      return { top: "season", draftFormat: "auction", draftMode: "live", seasonMode: "mock" };
    case "NO_DRAFT_LIVE_SEASON":
      return { top: "season", draftFormat: "auction", draftMode: "live", seasonMode: "live" };
    default:
      // Phase 1 legacy leagues (Gotcha d): fall back to LIVE_DRAFT_LIVE_SEASON.
      return { top: "draft", draftFormat: "auction", draftMode: "live", seasonMode: "mock" };
  }
};

// [Item 1] Team-count range + parity, driven by what the league actually is.
//
// Football basis: Head-to-Head needs an EVEN count so every manager has a
// weekly opponent. Points Only ranks on total points, so odd counts are
// football-legal - but a SEASON league runs the schedule/playoff bootstrap,
// which has not been audited for odd counts yet, so Points Only stays even
// until that banked work lands. A pure draft has no weekly opponent and no
// standings, so ANY count is fine there.
export const MIN_TEAMS = 2;
export const MAX_TEAMS = 14;

export const teamCountOptions = (form) => {
  const evensOnly = form?.top === "season";
  const out = [];
  for (let n = MIN_TEAMS; n <= MAX_TEAMS; n += 1) {
    if (evensOnly && n % 2 !== 0) continue;
    out.push(n);
  }
  return out;
};

const Radio = ({ checked, onClick, disabled, label, sub, dataTestId }) => (  <button
    type="button"
    onClick={disabled ? undefined : onClick}
    disabled={disabled}
    data-testid={dataTestId}
    className={`w-full text-left flex items-start gap-3 rounded-lg border-2 px-4 py-3 transition-all ${
      disabled
        ? "border-[#252525] bg-[#1C1C1C]/40 opacity-40 cursor-not-allowed"
        : checked
        ? "border-[#FF6B00] bg-[#FF6B00]/10"
        : "border-[#252525] bg-[#1C1C1C] hover:border-[#FF6B00]/40"
    }`}
  >
    <span
      className={`mt-1 inline-block h-4 w-4 rounded-full border-2 shrink-0 ${
        checked ? "border-[#FF6B00] bg-[#FF6B00]" : "border-[#A0A0A0]"
      }`}
    />
    <span className="flex-1">
      <span className="block font-display font-bold text-base text-[#F5F5F5]">
        {label}
        {disabled && (
          <span className="ml-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-[#A0A0A0] font-body">
            <Lock size={10} /> Coming Soon
          </span>
        )}
      </span>
      {sub && <span className="block font-body text-xs text-[#A0A0A0] mt-0.5">{sub}</span>}
    </span>
  </button>
);

export default function Step1LeagueType({ form, update, onOpenCopyModal }) {
  return (
    <div data-testid="wizard-step-1" className="space-y-8">
      {/* [§6.2] Copy settings from a prior league - wizard-level entry point */}
      {onOpenCopyModal && (
        <section className="rounded-xl border border-[#FF6B00]/30 bg-[#FF6B00]/[0.04] p-4">
          <p className="font-display font-black text-[10px] uppercase tracking-widest text-[#FF6B00] mb-2">
            Returning Commissioner?
          </p>
          <button
            type="button"
            onClick={onOpenCopyModal}
            data-testid="copy-settings-from-prior-btn"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#1C1C1C] border border-[#FF6B00]/40 hover:border-[#FF6B00] hover:bg-[#FF6B00]/10 transition-all font-display font-bold text-sm text-[#F5F5F5]"
          >
            <Copy size={14} className="text-[#FF6B00]" />
            Copy settings from a prior league →
          </button>
          <p className="font-body text-[11px] text-[#888] mt-2 leading-relaxed">
            Pre-fills the wizard with roster, scoring, draft format, and timers from a league you commissioned previously. You'll still set a fresh name, invite list, keepers, and draft date.
          </p>
        </section>
      )}

      <header>
        <h2 className="font-display font-black text-3xl uppercase text-[#F5F5F5]">
          League Type & Setup
        </h2>
        <p className="font-body text-sm text-[#A0A0A0] mt-1">
          What kind of league are you running?
        </p>
      </header>

      {/* Tier 1: Draft vs Season */}
      <section>
        <h3 className="font-display font-bold text-sm uppercase tracking-wider text-[#A0A0A0] mb-3">
          What are you setting up?
        </h3>
        <div className="grid sm:grid-cols-2 gap-3">
          <Radio
            checked={form.top === "draft"}
            onClick={() => update({ top: "draft" })}
            label="Draft"
            sub="Start with a draft (auction or snake), optionally play a season after."
            dataTestId="wizard-top-draft"
          />
          <Radio
            checked={form.top === "season"}
            onClick={() => update({
              top: "season",
              // Season leagues pair up weekly, so an odd count carried over
              // from a draft-only setup has to round up.
              num_teams: form.num_teams % 2 === 0 ? form.num_teams : form.num_teams + 1,
            })}
            label="Season"
            sub="Skip the draft, start a season directly."
            dataTestId="wizard-top-season"
          />
        </div>
      </section>

      {/* Tier 2a: Draft branch */}
      {form.top === "draft" && (
        <>
          <section>
            <h3 className="font-display font-bold text-sm uppercase tracking-wider text-[#A0A0A0] mb-3">
              Draft Format
            </h3>
            <div className="grid sm:grid-cols-2 gap-3">
              <Radio
                checked={form.draftFormat === "auction"}
                onClick={() => update({ draftFormat: "auction" })}
                label="Auction"
                sub="The PGF flagship - bid your budget, pick your moments."
                dataTestId="wizard-draft-format-auction"
              />
              <Radio
                checked={form.draftFormat === "snake"}
                onClick={() => update({ draftFormat: "snake" })}
                label="Snake"
                sub="Pick-by-pick serpentine - every manager picks once per round, order reverses."
                dataTestId="wizard-draft-format-snake"
              />
            </div>
          </section>

          {/* R10: collapsed 3-option radio for Live/Mock+Season/Mock only */}
          <section>
            <h3 className="font-display font-bold text-sm uppercase tracking-wider text-[#A0A0A0] mb-3">
              Live or Mock?
            </h3>
            <div className="grid gap-3">
              <Radio
                checked={form.draftMode === "live"}
                onClick={() => update({ draftMode: "live" })}
                label="Live"
                sub="Real people, real consequences."
                dataTestId="wizard-draft-mode-live"
              />
              <Radio
                checked={form.draftMode === "mock_season"}
                onClick={() => update({ draftMode: "mock_season" })}
                label="Mock + Season"
                sub="Practice draft, then mock season plays out."
                dataTestId="wizard-draft-mode-mock-season"
              />
              <Radio
                checked={form.draftMode === "mock_only"}
                onClick={() => update({ draftMode: "mock_only" })}
                label="Mock only"
                sub="Practice draft, archive after."
                dataTestId="wizard-draft-mode-mock-only"
              />
            </div>
          </section>
        </>
      )}

      {/* Tier 2b: Season branch */}
      {form.top === "season" && (
        <section>
          <h3 className="font-display font-bold text-sm uppercase tracking-wider text-[#A0A0A0] mb-3">
            Season Mode
          </h3>
          <div className="grid sm:grid-cols-2 gap-3">
            <Radio
              checked={false}
              disabled
              label="Live"
              sub="Requires roster import - Phase 2d/e."
              dataTestId="wizard-season-mode-live"
            />
            <Radio
              checked={form.seasonMode === "mock"}
              onClick={() => update({ seasonMode: "mock" })}
              label="Mock"
              sub="Simulated season with random or selected rosters."
              dataTestId="wizard-season-mode-mock"
            />
          </div>

          {/* [Item 1] League Type - only a SEASON needs one. It decides how
              the season is won, and therefore whether the team count has to
              pair up every week. */}
          <h3 className="font-display font-bold text-sm uppercase tracking-wider text-[#A0A0A0] mt-6 mb-3">
            League Type
          </h3>
          <div className="grid sm:grid-cols-2 gap-3">
            <Radio
              checked={form.seasonLeagueType === "h2h"}
              onClick={() => update({
                seasonLeagueType: "h2h",
                num_teams: form.num_teams % 2 === 0 ? form.num_teams : form.num_teams + 1,
              })}
              label="Head-to-Head"
              sub="Weekly matchups - you beat one manager or you lose to them. Needs an even count so nobody sits out."
              dataTestId="wizard-season-league-type-h2h"
            />
            <Radio
              checked={form.seasonLeagueType === "points"}
              onClick={() => update({ seasonLeagueType: "points" })}
              label="Points Only"
              sub="No opponents - every team is ranked on total points scored all season."
              dataTestId="wizard-season-league-type-points"
            />
          </div>
        </section>
      )}

      {/* League Name + Commissioner Team Name + Number of Teams */}
      <section className="grid sm:grid-cols-2 gap-6 pt-4 border-t border-[#252525]">
        <div>
          <label className="block font-body text-sm font-medium mb-2 text-[#A0A0A0]">
            League Name <span className="text-[#FF6B00]">*</span>
          </label>
          <input
            data-testid="wizard-league-name-input"
            value={form.name}
            maxLength={60}
            onChange={(e) => update({ name: e.target.value })}
            placeholder="The Thunderdome 2026"
            className="w-full bg-[#1C1C1C] border border-[#252525] focus:border-[#FF6B00] rounded-lg px-4 py-3 font-body text-base outline-none transition-colors text-[#F5F5F5] placeholder-[#A0A0A0]/50"
          />
          <p className="font-body text-xs text-[#A0A0A0] mt-1">
            {form.name.length}/60
          </p>
        </div>
        {/* [Phase 2c-D §6.7 NEW-6] Commissioner Team Name */}
        <div>
          <label className="block font-body text-sm font-medium mb-2 text-[#A0A0A0]">
            Your Team Name
          </label>
          <input
            data-testid="wizard-commissioner-team-name-input"
            value={form.commissioner_team_name || ""}
            maxLength={40}
            onChange={(e) => update({ commissioner_team_name: e.target.value })}
            placeholder="The Dynasty Killers"
            className="w-full bg-[#1C1C1C] border border-[#252525] focus:border-[#FF6B00] rounded-lg px-4 py-3 font-body text-base outline-none transition-colors text-[#F5F5F5] placeholder-[#A0A0A0]/50"
          />
          <p className="font-body text-xs text-[#A0A0A0] mt-1">
            Optional. Leave blank to use your first name.
          </p>
        </div>
        <div>
          <label className="block font-body text-sm font-medium mb-2 text-[#A0A0A0]">
            Number of Teams
          </label>
          <select
            data-testid="wizard-num-teams-select"
            value={form.num_teams}
            onChange={(e) => update({ num_teams: parseInt(e.target.value, 10) })}
            className="w-full bg-[#1C1C1C] border border-[#252525] focus:border-[#FF6B00] rounded-lg px-4 py-3 font-body text-base outline-none transition-colors text-[#F5F5F5]"
          >
            {/* [RL-55] MIN=2, MAX=14 per A.2.2 v1.1 spec. 2-team minimum
                unlocks intimate head-to-head leagues + simplifies smoke
                testing. Old 16 dropped - never used by the production cohort
                and exceeds the playoff bracket size we ship. */}
            {teamCountOptions(form).map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
          <p className="font-body text-xs text-[#A0A0A0] mt-1" data-testid="wizard-num-teams-hint">
            {form.top === "season"
              ? "Season leagues use even counts so every week pairs up."
              : "Any count from 2 to 14 - a draft needs no weekly opponent."}
          </p>
        </div>
      </section>
    </div>
  );
}

export const validateStep1 = (form) => {
  if (!form.top) return "Choose Draft or Season.";
  if (form.top === "draft" && !form.draftMode) return "Choose Live or Mock.";
  if (form.top === "season" && form.seasonMode !== "mock") return "Live Season is coming soon - pick Mock.";
  if (form.top === "season" && !form.seasonLeagueType) return "Choose Head-to-Head or Points Only.";
  if (!form.name.trim()) return "League name is required.";
  if (form.name.length > 60) return "League name max 60 chars.";
  if (!teamCountOptions(form).includes(form.num_teams)) {
    return form.top === "season"
      ? "Season leagues need an even number of teams."
      : "Pick a team count between 2 and 14.";
  }
  return null;
};
